import React, { useEffect, useState, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const CATEGORIES = [
  'HVAC', 'ELECTRICAL', 'PLUMBING', 'ELEVATOR', 'GENERATOR',
  'FURNITURE', 'KITCHEN_EQUIP', 'LAUNDRY_EQUIP', 'OTHER'
];

const LOCATION_TYPES = ['ROOM', 'COMMON_AREA', 'BACK_OF_HOUSE'];

const STATUS_STYLES = {
  ACTIVE:          { background: '#e6f9ed', color: '#1a7f37', border: '1px solid #a3d9b1' },
  UNDER_REPAIR:    { background: '#fff4e5', color: '#b35c00', border: '1px solid #f5c26b' },
  DECOMMISSIONED:  { background: '#f0f0f0', color: '#666',    border: '1px solid #ccc' },
  DISPOSED:        { background: '#fde8e8', color: '#c0392b', border: '1px solid #e8a0a0' }
};

const EMPTY_FORM = {
  asset_code: '', asset_name: '', category: 'HVAC', location_type: 'ROOM',
  room_id: '', location_desc: '', make: '', model: '', serial_number: '',
  purchase_date: '', warranty_expiry: '', expected_life_years: '',
  vendor_id: '', purchase_cost: '', current_value: ''
};

function MaintAssets({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [assets, setAssets] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Dropdowns
  const [rooms, setRooms] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Detail view
  const [detailAsset, setDetailAsset] = useState(null);
  const [lifecycleLog, setLifecycleLog] = useState([]);
  const [showDetail, setShowDetail] = useState(false);

  const headers = useCallback(() => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id
  }), [token, selectedProperty]);

  const jsonHeaders = useCallback(() => ({
    ...headers(),
    'Content-Type': 'application/json'
  }), [headers]);

  /* ---------- data loading ---------- */

  const loadAssets = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.set('category', filterCategory);
      if (filterStatus) params.set('status', filterStatus);
      const qs = params.toString() ? '?' + params.toString() : '';
      const res = await fetch('/api/v1/maintenance/assets' + qs, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to load assets'); setAssets([]); return; }
      setAssets(Array.isArray(data) ? data : (data.rows || []));
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, filterCategory, filterStatus, headers]);

  const loadRooms = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/maintenance/rooms', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (res.ok) setRooms(Array.isArray(data) ? data : (data.rows || []));
    } catch { /* ignore */ }
  }, [selectedProperty, headers]);

  const loadVendors = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/maintenance/vendors', { headers: headers() });
      const data = await res.json().catch(() => ([]));
      if (res.ok) setVendors(Array.isArray(data) ? data : (data.rows || []));
    } catch { /* ignore */ }
  }, [selectedProperty, headers]);

  useEffect(() => { loadAssets(); }, [loadAssets]);
  useEffect(() => { loadRooms(); loadVendors(); }, [loadRooms, loadVendors]);

  /* ---------- detail view ---------- */

  const openDetail = async (asset) => {
    setError('');
    try {
      const res = await fetch('/api/v1/maintenance/assets/' + asset.id, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to load asset details'); return; }
      setDetailAsset(data.asset || data);
      setLifecycleLog(Array.isArray(data.lifecycle_log) ? data.lifecycle_log : []);
      setShowDetail(true);
    } catch (err) {
      setError(err?.message || 'Network error');
    }
  };

  /* ---------- create / edit ---------- */

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const openEdit = (asset, e) => {
    e.stopPropagation();
    setForm({
      asset_code: asset.asset_code || '',
      asset_name: asset.asset_name || '',
      category: asset.category || 'HVAC',
      location_type: asset.location_type || 'ROOM',
      room_id: asset.room_id || '',
      location_desc: asset.location_desc || '',
      make: asset.make || '',
      model: asset.model || '',
      serial_number: asset.serial_number || '',
      purchase_date: asset.purchase_date ? asset.purchase_date.substring(0, 10) : '',
      warranty_expiry: asset.warranty_expiry ? asset.warranty_expiry.substring(0, 10) : '',
      expected_life_years: asset.expected_life_years ?? '',
      vendor_id: asset.vendor_id || '',
      purchase_cost: asset.purchase_cost ?? '',
      current_value: asset.current_value ?? ''
    });
    setEditingId(asset.id);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!form.asset_code.trim() || !form.asset_name.trim()) {
      setError('Asset Code and Asset Name are required.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = { ...form };
      if (payload.room_id === '') delete payload.room_id;
      if (payload.vendor_id === '') delete payload.vendor_id;
      if (payload.purchase_cost === '') delete payload.purchase_cost;
      if (payload.current_value === '') delete payload.current_value;
      if (payload.expected_life_years === '') delete payload.expected_life_years;

      const url = editingId
        ? '/api/v1/maintenance/assets/' + editingId
        : '/api/v1/maintenance/assets';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: jsonHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Save failed'); return; }
      setSuccess(editingId ? 'Asset updated.' : 'Asset created.');
      setShowModal(false);
      loadAssets();
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const fmtDate = (v) => {
    if (!v) return '-';
    const s = typeof v === 'string' ? v.substring(0, 10) : '';
    return s || '-';
  };

  const fmtCurrency = (v) => {
    if (v == null || v === '') return '-';
    return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  /* ---------- styles ---------- */

  const S = {
    page: { padding: 24, fontFamily: 'Inter, sans-serif', maxWidth: 1200, margin: '0 auto' },
    h1: { fontSize: 22, fontWeight: 700, margin: 0, marginBottom: 4 },
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    filters: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
    select: { padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 },
    btn: { padding: '8px 16px', borderRadius: 4, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
    btnSm: { padding: '4px 10px', borderRadius: 4, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', fontSize: 12, cursor: 'pointer', fontWeight: 500 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '10px 8px', background: '#f4f5f7', borderBottom: '2px solid #ddd', fontWeight: 600, fontSize: 12, textTransform: 'uppercase', color: '#555' },
    td: { padding: '10px 8px', borderBottom: '1px solid #eee' },
    trHover: { cursor: 'pointer' },
    badge: (status) => ({
      display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      ...(STATUS_STYLES[status] || STATUS_STYLES.ACTIVE)
    }),
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 40, zIndex: 1000 },
    modal: { background: '#fff', borderRadius: 8, padding: isMobile ? 16 : 24, width: isMobile ? '100%' : 620, maxWidth: '95vw', ...(isMobile ? { boxSizing: 'border-box' } : {}), maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.18)' },
    modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 16 },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    formGroupFull: { display: 'flex', flexDirection: 'column', gap: 4, gridColumn: '1 / -1' },
    label: { fontSize: 12, fontWeight: 600, color: '#555' },
    input: { padding: '7px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 },
    errBox: { background: '#fde8e8', color: '#c0392b', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 13 },
    sucBox: { background: '#e6f9ed', color: '#1a7f37', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 13 },
    detailSection: { marginBottom: 16 },
    detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 13 },
    detailLabel: { fontWeight: 600, color: '#555', fontSize: 12 },
    detailVal: { marginBottom: 4 },
    logTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 },
    logTh: { textAlign: 'left', padding: '6px 8px', background: '#f9f9f9', borderBottom: '1px solid #ddd', fontWeight: 600 },
    logTd: { padding: '6px 8px', borderBottom: '1px solid #f0f0f0' },
    noData: { textAlign: 'center', padding: 40, color: '#999', fontSize: 14 }
  };

  /* ---------- render ---------- */

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <div>
          <h1 style={S.h1}>Asset Registry</h1>
          <span style={{ fontSize: 13, color: '#777' }}>Manage property assets, equipment and their lifecycle</span>
        </div>
        <button style={S.btn} onClick={openCreate}>+ New Asset</button>
      </div>

      {error && <div style={S.errBox}>{error}</div>}
      {success && <div style={S.sucBox}>{success}</div>}

      {/* Filters */}
      <div style={S.filters}>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Category</label>
        <select style={S.select} value={filterCategory} onChange={e => { setFilterCategory(e.target.value); }}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </select>
        <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginLeft: 8 }}>Status</label>
        <select style={S.select} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); }}>
          <option value="">All Statuses</option>
          {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={S.noData}>Loading...</div>
      ) : assets.length === 0 ? (
        <div style={S.noData}>No assets found.</div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Asset Code</th>
                <th style={S.th}>Asset Name</th>
                <th style={S.th}>Category</th>
                <th style={S.th}>Location</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Last Service</th>
                <th style={S.th}>Warranty Expiry</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assets.map(a => (
                <tr key={a.id} style={S.trHover} onClick={() => openDetail(a)}>
                  <td style={S.td}>{a.asset_code}</td>
                  <td style={{ ...S.td, fontWeight: 600 }}>{a.asset_name}</td>
                  <td style={S.td}>{(a.category || '').replace(/_/g, ' ')}</td>
                  <td style={S.td}>{a.location_desc || (a.location_type || '').replace(/_/g, ' ')}</td>
                  <td style={S.td}><span style={S.badge(a.status)}>{(a.status || 'ACTIVE').replace(/_/g, ' ')}</span></td>
                  <td style={S.td}>{fmtDate(a.last_service_date)}</td>
                  <td style={S.td}>{fmtDate(a.warranty_expiry)}</td>
                  <td style={S.td}>
                    <button style={S.btnSm} onClick={(e) => openEdit(a, e)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={S.overlay} onClick={() => setShowModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{editingId ? 'Edit Asset' : 'New Asset'}</div>
            {error && <div style={S.errBox}>{error}</div>}

            <div style={S.formGrid}>
              <div style={S.formGroup}>
                <label style={S.label}>Asset Code *</label>
                <input style={S.input} value={form.asset_code}
                  onChange={e => setForm(f => ({ ...f, asset_code: e.target.value }))} />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Asset Name *</label>
                <input style={S.input} value={form.asset_name}
                  onChange={e => setForm(f => ({ ...f, asset_name: e.target.value }))} />
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Category</label>
                <select style={S.input} value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Location Type</label>
                <select style={S.input} value={form.location_type}
                  onChange={e => setForm(f => ({ ...f, location_type: e.target.value, room_id: '' }))}>
                  {LOCATION_TYPES.map(l => <option key={l} value={l}>{l.replace(/_/g, ' ')}</option>)}
                </select>
              </div>

              {form.location_type === 'ROOM' && (
                <div style={S.formGroup}>
                  <label style={S.label}>Room</label>
                  <select style={S.input} value={form.room_id}
                    onChange={e => setForm(f => ({ ...f, room_id: e.target.value }))}>
                    <option value="">-- Select Room --</option>
                    {rooms.map(r => (
                      <option key={r.room_id || r.id} value={r.room_id || r.id}>
                        {r.room_number || r.room_name || r.room_id || r.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={form.location_type === 'ROOM' ? S.formGroup : S.formGroupFull}>
                <label style={S.label}>Location Description</label>
                <input style={S.input} value={form.location_desc}
                  onChange={e => setForm(f => ({ ...f, location_desc: e.target.value }))} />
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Make</label>
                <input style={S.input} value={form.make}
                  onChange={e => setForm(f => ({ ...f, make: e.target.value }))} />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Model</label>
                <input style={S.input} value={form.model}
                  onChange={e => setForm(f => ({ ...f, model: e.target.value }))} />
              </div>

              <div style={S.formGroupFull}>
                <label style={S.label}>Serial Number</label>
                <input style={S.input} value={form.serial_number}
                  onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Purchase Date</label>
                <input style={S.input} type="date" value={form.purchase_date}
                  onChange={e => setForm(f => ({ ...f, purchase_date: e.target.value }))} />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Warranty Expiry</label>
                <input style={S.input} type="date" value={form.warranty_expiry}
                  onChange={e => setForm(f => ({ ...f, warranty_expiry: e.target.value }))} />
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Expected Life (Years)</label>
                <input style={S.input} type="number" min="0" value={form.expected_life_years}
                  onChange={e => setForm(f => ({ ...f, expected_life_years: e.target.value }))} />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Vendor</label>
                <select style={S.input} value={form.vendor_id}
                  onChange={e => setForm(f => ({ ...f, vendor_id: e.target.value }))}>
                  <option value="">-- Select Vendor --</option>
                  {vendors.map(v => (
                    <option key={v.vendor_id || v.id} value={v.vendor_id || v.id}>
                      {v.vendor_name || v.name || v.vendor_id || v.id}
                    </option>
                  ))}
                </select>
              </div>

              <div style={S.formGroup}>
                <label style={S.label}>Purchase Cost</label>
                <input style={S.input} type="number" min="0" step="0.01" value={form.purchase_cost}
                  onChange={e => setForm(f => ({ ...f, purchase_cost: e.target.value }))} />
              </div>
              <div style={S.formGroup}>
                <label style={S.label}>Current Value</label>
                <input style={S.input} type="number" min="0" step="0.01" value={form.current_value}
                  onChange={e => setForm(f => ({ ...f, current_value: e.target.value }))} />
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button style={{ ...S.btnSm, border: '1px solid #ccc', color: '#555' }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...S.btn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={handleSave}>
                {saving ? 'Saving...' : editingId ? 'Update Asset' : 'Create Asset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && detailAsset && (
        <div style={S.overlay} onClick={() => setShowDetail(false)}>
          <div style={{ ...S.modal, width: isMobile ? '100%' : 680 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={S.modalTitle}>
                {detailAsset.asset_name}
                <span style={{ ...S.badge(detailAsset.status), marginLeft: 10, fontSize: 12 }}>
                  {(detailAsset.status || 'ACTIVE').replace(/_/g, ' ')}
                </span>
              </div>
              <button style={{ ...S.btnSm, border: '1px solid #ccc', color: '#555' }} onClick={() => setShowDetail(false)}>Close</button>
            </div>

            <div style={S.detailSection}>
              <div style={S.detailGrid}>
                <div><span style={S.detailLabel}>Asset Code</span><div style={S.detailVal}>{detailAsset.asset_code}</div></div>
                <div><span style={S.detailLabel}>Category</span><div style={S.detailVal}>{(detailAsset.category || '').replace(/_/g, ' ')}</div></div>
                <div><span style={S.detailLabel}>Location Type</span><div style={S.detailVal}>{(detailAsset.location_type || '').replace(/_/g, ' ')}</div></div>
                <div><span style={S.detailLabel}>Location</span><div style={S.detailVal}>{detailAsset.location_desc || '-'}</div></div>
                <div><span style={S.detailLabel}>Make</span><div style={S.detailVal}>{detailAsset.make || '-'}</div></div>
                <div><span style={S.detailLabel}>Model</span><div style={S.detailVal}>{detailAsset.model || '-'}</div></div>
                <div><span style={S.detailLabel}>Serial Number</span><div style={S.detailVal}>{detailAsset.serial_number || '-'}</div></div>
                <div><span style={S.detailLabel}>Purchase Date</span><div style={S.detailVal}>{fmtDate(detailAsset.purchase_date)}</div></div>
                <div><span style={S.detailLabel}>Warranty Expiry</span><div style={S.detailVal}>{fmtDate(detailAsset.warranty_expiry)}</div></div>
                <div><span style={S.detailLabel}>Expected Life</span><div style={S.detailVal}>{detailAsset.expected_life_years ? detailAsset.expected_life_years + ' years' : '-'}</div></div>
                <div><span style={S.detailLabel}>Purchase Cost</span><div style={S.detailVal}>{fmtCurrency(detailAsset.purchase_cost)}</div></div>
                <div><span style={S.detailLabel}>Current Value</span><div style={S.detailVal}>{fmtCurrency(detailAsset.current_value)}</div></div>
                <div><span style={S.detailLabel}>Vendor</span><div style={S.detailVal}>{detailAsset.vendor_name || detailAsset.vendor_id || '-'}</div></div>
                <div><span style={S.detailLabel}>Last Service</span><div style={S.detailVal}>{fmtDate(detailAsset.last_service_date)}</div></div>
              </div>
            </div>

            {/* Lifecycle Log */}
            <div style={S.detailSection}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Lifecycle Log</h3>
              {lifecycleLog.length === 0 ? (
                <div style={{ fontSize: 13, color: '#999' }}>No lifecycle events recorded.</div>
              ) : (
                <table style={S.logTable}>
                  <thead>
                    <tr>
                      <th style={S.logTh}>Date</th>
                      <th style={S.logTh}>Event</th>
                      <th style={S.logTh}>Description</th>
                      <th style={S.logTh}>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lifecycleLog.map((log, idx) => (
                      <tr key={log.id || idx}>
                        <td style={S.logTd}>{fmtDate(log.event_date || log.created_at)}</td>
                        <td style={S.logTd}>
                          <span style={S.badge(log.event_type === 'SERVICE' ? 'ACTIVE' : log.event_type === 'REPAIR' ? 'UNDER_REPAIR' : log.event_type === 'DECOMMISSION' ? 'DECOMMISSIONED' : 'ACTIVE')}>
                            {(log.event_type || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td style={S.logTd}>{log.description || log.remarks || '-'}</td>
                        <td style={S.logTd}>{log.performed_by || log.created_by || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintAssets;
